import type { Treaty } from '@elysiajs/eden';
import { queryOptions } from '@tanstack/react-query';
import type {
  AssetCatalogMode,
  AssetCatalogModes,
  AssetCatalogOwnerType,
  CatalogDefinition,
} from 'realtiq-api/src/utils/asset-catalog';

import { api } from '@/lib/treaty';

export class AssetCatalogRequestError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
  }
}

async function extractCatalogData<T extends Record<number, unknown>>(
  promise: Promise<Treaty.TreatyResponse<T>>
) {
  const { data, error, response, status } = await promise;
  if (error !== null) {
    const value = error.value;
    const message =
      typeof value === 'string'
        ? value
        : value &&
            typeof value === 'object' &&
            'message' in value &&
            typeof value.message === 'string'
          ? value.message
          : response.statusText;
    throw new AssetCatalogRequestError(status, message || 'Something went wrong');
  }
  if (data === null || data === undefined)
    throw new AssetCatalogRequestError(status, 'Unexpected empty catalog response');
  return data;
}

const catalogEndpoint = (type: AssetCatalogOwnerType, id: string) =>
  type === 'project' ? api.projects({ id })['asset-catalog'] : api.estates({ id })['asset-catalog'];

export const getCatalogOptions = (type: AssetCatalogOwnerType, id: string) =>
  queryOptions({
    queryKey: ['asset-catalog', type, id],
    queryFn: () => extractCatalogData(catalogEndpoint(type, id).get()),
  });

export const saveCatalog = (
  type: AssetCatalogOwnerType,
  id: string,
  revision: number,
  configuration: AssetCatalogModes
) => extractCatalogData(catalogEndpoint(type, id).put({ revision, configuration }));

export const saveCatalogPreset = (
  type: AssetCatalogOwnerType,
  id: string,
  body: {
    revision: number;
    modes: AssetCatalogMode[];
    configuration: CatalogDefinition;
    name: string;
    preset?: CatalogDefinition['sourcePreset'];
  }
) => extractCatalogData(catalogEndpoint(type, id)['save-preset'].post(body));

export const applyCatalogPreset = (
  type: AssetCatalogOwnerType,
  id: string,
  revision: number,
  presetId: string,
  modes: AssetCatalogMode[]
) =>
  extractCatalogData(catalogEndpoint(type, id)['apply-preset'].post({ revision, presetId, modes }));

export const resetEstateCatalog = (id: string, revision: number, modes: AssetCatalogMode[]) =>
  extractCatalogData(api.estates({ id })['asset-catalog'].reset.post({ revision, modes }));

const presetEndpoint = (tenantId: string) => api.tenants({ id: tenantId })['asset-catalog-presets'];

export const getCatalogPresetsOptions = (tenantId: string | null) =>
  queryOptions({
    queryKey: ['asset-catalog-presets', tenantId],
    queryFn: () => extractCatalogData(presetEndpoint(tenantId ?? '').get()),
    enabled: Boolean(tenantId),
  });

export const getAllFurnitureOptions = () =>
  queryOptions({
    queryKey: ['asset-catalog-furniture-items'],
    queryFn: () => extractCatalogData(api.furniture.catalog.items.get()),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
